import { Alert, AlertDescription, Button, SliderModal } from '@see-ui/core';
import { CirclePlus } from 'lucide-react';
import React from 'react';
import AddPaymentMethodsSlider from './AddPaymentMethodsSlider';
import * as service from '@services/api/member-pharmacy-service';
import { useGlobalStore } from '@services/store/global-store';
import EditCardsSlider from './EditCardsSlider';

const EditPaymentMethodsSlider: React.FC<{
  onClose: () => void;
  paymentMethods: any;
  onUpdatePaymentMethods: any;
  onSelectCard?: (card: any) => void;
}> = ({ onClose, paymentMethods, onUpdatePaymentMethods, onSelectCard }) => {
  const { memberDetails } = useGlobalStore((state) => state);
  const [editCardSliderOpen, setEditCardSliderOpen] = React.useState(false);
  const [selectedCard, setSelectedCard] = React.useState<any>(null);
  const [error, setError] = React.useState<string | null>(null);

  const [addSliderOpen, setAddSliderOpen] = React.useState(false);
  const cardList = Array.isArray(paymentMethods)
    ? paymentMethods.filter((pm) => pm.card)
    : [];
  const bankAccountList = Array.isArray(paymentMethods)
    ? paymentMethods.filter((pm) => pm.bankAccount)
    : [];

  const handlePrimaryForcards = async (pm: any) => {
    setError(null);
    const updateReq = {
      action: 'UPD',
      paymentMethod: {
        ...pm,
        isDefaultPaymentMethod: true
      }
    };
    const req = {
      hcid: memberDetails?.member?.memberPolicy?.hcid,
      updateRequest: updateReq
    };

    const updateResp = await service.addUpdatePaymentMethod(req);
    if (updateResp?.data) {
      // console.log('Payment method updated successfully');
      //load the data
      const getPaymentMethodResp = await service.getPaymentMethod({
        hcid: memberDetails?.member?.memberPolicy?.hcid
      });
      if (getPaymentMethodResp?.data) {
        onUpdatePaymentMethods(getPaymentMethodResp.data?.paymentMethods);
      }
      onClose();
    } else {
      // console.log('Failed to update payment method');
      setError(
        `Sorry, looks like something isn't working. Please give us some time to fix it. Then, try again.`
      );
    }
  };

  const handleDelete = async (tokenId: string) => {
    const deleteReq = {
      action: 'DEL',
      paymentMethod: {
        tokenId: tokenId
      }
    };
    const req = {
      hcid: memberDetails?.member?.memberPolicy?.hcid,
      deleteRequest: deleteReq
    };

    const deleteResp = await service.deletePaymentMethod(req);
    if (deleteResp?.data) {
      console.log('Payment method deleted successfully');
      //load the data
      const getPaymentMethodResp = await service.getPaymentMethod({
        hcid: memberDetails?.member?.memberPolicy?.hcid
      });
      if (getPaymentMethodResp?.data) {
        onUpdatePaymentMethods(getPaymentMethodResp.data?.paymentMethods);
      }
    } else {
      console.log('Failed to delete payment method');
    }
  };

  return (
    <>
      <SliderModal
        direction="right"
        labelAriaCloseBtn="Close Modal"
        onClose={onClose}
        open
      >
        <SliderModal.Body>
          <div className="px-10">
            <h2> Edit Payment Method</h2>

            <div className="py-2">
              <Alert variant="info">
                <React.Fragment key=".0">
                  <AlertDescription className="text-sm">
                    To delete your existing payment method, add a new card or
                    select an existing card and then set it as your primary
                    payment method.
                  </AlertDescription>
                </React.Fragment>
              </Alert>
            </div>
            {error && (
              <div className="py-2">
                <Alert variant="error">
                  <React.Fragment key=".0">
                    <AlertDescription className="text-sm">
                      {error}
                    </AlertDescription>
                  </React.Fragment>
                </Alert>
              </div>
            )}

            {cardList?.length >= 5 && (
              <div className="pt-2 pb-5">
                <Alert variant="info">
                  <React.Fragment key=".0">
                    <AlertDescription className="text-sm">
                      You cannot add any more payment methods, you have the
                      maximum of five. Please update, or remove an existing
                      method.
                    </AlertDescription>
                  </React.Fragment>
                </Alert>
              </div>
            )}

            <h6>Credit and Debit Cards</h6>

            {cardList.length === 0 && <li>No cards found.</li>}
            <div className="mt-5 flex flex-wrap gap-9">
              {cardList.map((pm, index) => (
                <div
                  key={index}
                  className="flex flex-col items-start border p-6 rounded shadow-sm w-1/4" // Adjusted width to 1/4 for smaller boxes
                >
                  <p className="font-semibold pb-1">
                    {pm.card.cardSubType}-{pm.card.cardNumber?.slice(-4)}
                  </p>
                  <p className="pb-4">
                    Expires {pm.card.expirationMonth}/{pm.card.expirationYear}
                  </p>
                  {pm.isDefaultPaymentMethod && (
                    <span className="text-green-600 font-bold pb-4">
                      Primary
                    </span>
                  )}

                  {!pm.isDefaultPaymentMethod && (
                    <Button
                      variant="secondary"
                      size="sm"
                      className="w-full  mt-2"
                      rounded
                      onClick={() => handlePrimaryForcards(pm)}
                    >
                      Set as primary
                    </Button>
                  )}

                  <Button
                    variant="secondary"
                    size="sm"
                    className="w-full mt-2"
                    rounded
                    onClick={() => {
                      setSelectedCard(pm);
                      setEditCardSliderOpen(true);
                    }}
                  >
                    Edit
                  </Button>
                  {!pm.isDefaultPaymentMethod && (
                    <Button
                      variant="link"
                      size="sm"
                      className="w-full mt-auto"
                      rounded
                      onClick={() => handleDelete(pm.tokenId)}
                    >
                      Delete
                    </Button>
                  )}

                  {true && (
                    <Button
                      variant="link"
                      size="sm"
                      className="w-full mt-auto"
                      rounded
                      onClick={() => {
                        if (onSelectCard) {
                          onSelectCard(pm);
                        }
                        onClose();
                      }}
                    >
                      Use this card
                    </Button>
                  )}
                </div>
              ))}
            </div>

            <h6>Bank Accounts</h6>

            {bankAccountList.length === 0 && <li>No bank accounts found.</li>}

            <div className="mt-5 flex flex-wrap gap-9">
              {bankAccountList.map((pm, index) => (
                <div
                  key={index}
                  className="flex flex-col items-start border p-2 rounded shadow-sm w-1/4" // Adjusted width to 1/4 for smaller boxes
                >
                  <p className="font-semibold pb-1">
                    {pm.bankAccount?.accountHolderName}
                  </p>
                  <p className="pb-1">{pm.bankAccount?.accountSubType}</p>

                  <p className="pb-5">Acct#:{pm.bankAccount?.accountNumber}</p>

                  <Button
                    variant="link"
                    size="sm"
                    className="w-full mt-auto"
                    rounded
                    onClick={() => handleDelete(pm.tokenId)}
                  >
                    Delete
                  </Button>
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-start ml-6">
            <Button
              variant="link"
              size="sm"
              rounded
              onClick={() => setAddSliderOpen(true)}
            >
              <CirclePlus size={16} />
              Add payment method for home delivery
            </Button>
          </div>

          {addSliderOpen && (
            <AddPaymentMethodsSlider
              onClose={() => setAddSliderOpen(false)}
              onUpdatePaymentMethods={onUpdatePaymentMethods}
            />
          )}

          {editCardSliderOpen && selectedCard && (
            <EditCardsSlider
              card={selectedCard}
              onClose={() => setEditCardSliderOpen(false)}
              onUpdatePaymentMethods={onUpdatePaymentMethods}
              // Pass other props as needed
            />
          )}
        </SliderModal.Body>
        <SliderModal.Footer>
          <Button onClick={onClose}>Close</Button>
        </SliderModal.Footer>
      </SliderModal>
    </>
  );
};

export default EditPaymentMethodsSlider;
